import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { HorizontalScroller } from "../../components";
import dayjs from "dayjs";
import { appointment, availability } from "../../store";
import { useToast } from "../toaster/toaster";
import "./rescheduleModal.css";

const RescheduleModal = ({
  isOpen,
  onClose,
  appointmentId,
  appointmentData,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  // const { data: availabilityData, isLoading: availabilityLoading } =
  //   useSelector((state) => state.availability);

  const { availability: availabilityData, availabilityLoading } = useSelector(
    (state) => state.availability
  );

  // State for selected date and time
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);

  // Generate available dates (next 7 days)
  useEffect(() => {
    if (isOpen) {
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = dayjs().add(i, "day");
        dates.push(date);
      }
      setAvailableDates(dates);

      // Set default selected date to today
      setSelectedDate(dates[0]);
    }
  }, [isOpen]);

  // Generate available times when date changes
  useEffect(() => {
    if (selectedDate && appointmentData?.topic?.id) {
      // Fetch availability for the selected date and topic
      dispatch(
        availability.getCoachesAvailabilityApi({
          topicId: appointmentData?.topic?.id,
          date: selectedDate.format("YYYY-MM-DD"),
        })
      );
    }
  }, [selectedDate, appointmentData?.topic?.id, dispatch]);

  // Update available times when availability data changes
  useEffect(() => {
    if (availabilityData && selectedDate) {
      // Convert the availability data to time slots
      const times = [];
      if (Array.isArray(availabilityData) && availabilityData.length > 0) {
        // Process each coach's availability
        availabilityData.forEach((coachData) => {
          // Filter to show only the original coach's availability
          if (coachData.coach?.id === appointmentData?.coach?.id) {
            if (
              coachData.availability &&
              Array.isArray(coachData.availability)
            ) {
              coachData.availability.forEach((slot) => {
                if (slot.startTime) {
                  const time = dayjs(
                    `${selectedDate.format("YYYY-MM-DD")} ${slot.startTime}`
                  );
                  times.push(time);
                }
              });
            }
          }
        });
      }
      setAvailableTimes(times);
    }
  }, [availabilityData, selectedDate, appointmentData?.coach?.id]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null); // Reset time when date changes

    // Immediately fetch availability for the selected date and topic
    // if (appointmentData?.topic?.id) {
    //   dispatch(
    //     availability.getCoachesAvailabilityApi({
    //       topicId: appointmentData?.topic?.id,
    //       date: date.format("YYYY-MM-DD"),
    //     })
    //   );
    // }
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Please select both date and time", "Validation Error");
      return;
    }

    try {
      // Find the availability slot for the selected date and time
      const selectedAvailability = availabilityData
        ?.find(
          (coachData) => coachData.coach?.id === appointmentData?.coach?.id
        )
        ?.availability?.find(
          (slot) =>
            slot.date === selectedDate.format("YYYY-MM-DD") &&
            slot.startTime === selectedTime.format("HH:mm")
        );

      if (!selectedAvailability) {
        toast.error("Selected time slot is not available", "Error");
        return;
      }

      // Call the reschedule API with the correct parameters
      await dispatch(
        appointment.rescheduleAppointment({
          appointmentId,
          availabilityId: selectedAvailability.id,
          topicId: appointmentData?.topic?.id,
          originalAppointmentId: appointmentId,
        })
      );

      toast.success("Appointment rescheduled successfully", "Success");
      onClose();

      // Refresh the bookings data
      dispatch(appointment.fetchUpcomingAppointments());
    } catch (error) {
      toast.error("Failed to reschedule appointment", "Error");
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={512}
      className="reschedule-modal"
      closable={true}
      closeIcon={<CloseOutlined />}
    >
      <div className="reschedule-modal-content">
        <h2 className="reschedule-modal-title">Reschedule Session</h2>

        <h3 className="reschedule-modal-subtitle">Select a new date</h3>
        <div className="date-selector">
          <HorizontalScroller>
            {availableDates.map((date, index) => (
              <div
                key={index}
                className={`date-chip ${
                  selectedDate?.isSame(date, "day") ? "active" : ""
                }`}
                onClick={() => handleDateSelect(date)}
              >
                <div className="date-card">
                  <div className="date-number">{date.format("DD")}</div>
                  <div className="date-month">{date.format("MMM")}</div>
                  <div className="date-day">{date.format("ddd")}</div>
                </div>
              </div>
            ))}
          </HorizontalScroller>
        </div>

        <h3 className="reschedule-modal-subtitle">Select a new time</h3>
        <div className="time-selector">
          {availabilityLoading ? (
            <p className="loading-text">Loading available times...</p>
          ) : availableTimes.length === 0 ? (
            <p className="no-times-text">No available times for this date</p>
          ) : (
            <HorizontalScroller>
              {availableTimes.map((time, index) => (
                <button
                  key={index}
                  className={`time-chip ${
                    selectedTime?.isSame(time) ? "active" : ""
                  }`}
                  onClick={() => handleTimeSelect(time)}
                >
                  <p className="time-text">{time.format("h:mm A")}</p>
                </button>
              ))}
            </HorizontalScroller>
          )}
        </div>

        <div className="reschedule-actions">
          <button
            type="button"
            className="reschedule-btn reschedule-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="reschedule-btn reschedule-btn-confirm"
            onClick={handleConfirm}
          >
            Confirm Reschedule
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default RescheduleModal;
